// - - - - - - - - - -
// Job install size ("qty" on the schedule tile) from the ActiveQueue
// `Materials[]` array.
//
// Extracted from `createScheduleArray` in schedule.context.js for SUP-10677:
// when FileMaker began setting material types automatically at invoice
// creation, tile materials started arriving as type "Tile" instead of the
// old dropdown value "Ceramic". The inline switch only knew "Ceramic", so a
// "Tile" material fell to `default` and contributed 0 — the tile showed only
// the customer's last name + store number (e.g. inv 2629209, 75 sqft).
//
// Rules (unchanged apart from the added "Tile"):
//   • Carpet            → total_yds
//   • Pad               → ignored (it sits under the carpet, not extra area)
//   • SQFT_TYPES below  → total_sqft
//   • anything else     → ignored (0). Deliberately NOT summed by default:
//     an unknown type could be underlayment/trim whose sqft would double-count.
//     Use dbtools/src/queries/activequeue-material-types.mjs to list the
//     types FileMaker actually sends before adding more here.
//
// Types are trimmed before matching — FileMaker sometimes sends "Carpet "
// (PRED-031).
// - - - - - - - - - -

const TYPE_KEY = "KI_AQ » Materials::type";
const YDS_KEY = "KI_AQ » Materials::total_yds";
const SQFT_KEY = "KI_AQ » Materials::total_sqft";

export const MATERIAL_SQFT_TYPES = [
  "Vinyl",
  "Plank",
  "Ceramic", // legacy dropdown value
  "Tile", // SUP-10677 — automated type set at invoice creation
  "Wood",
  "Wood/Lam", // SUP-10677 — automated wood/laminate type (Prod 10/31: 557 jobs had it as their only material, so qty showed 0)
  "Backsplash",
  "InteriorDoor",
  "ExteriorDoor",
  "StormDoor",
  "Window",
];

// Mirrors the old `parseFloat(x).toFixed(0)` + unary-plus coercion exactly:
// rounds to a whole number; NaN (missing/blank field) stays NaN.
const roundedNumber = (value) => +parseFloat(value).toFixed(0);

/**
 * Size contributed by a single Materials[] row.
 * @param {Object} material - one ActiveQueue Materials[] entry
 * @returns {number} yards (Carpet), sqft (hard surfaces) or 0 (Pad/unknown)
 */
export const getMaterialQty = (material) => {
  if (!material) return 0;
  const type = String(material[TYPE_KEY] ?? "").trim();
  if (type === "Carpet") return roundedNumber(material[YDS_KEY]);
  if (MATERIAL_SQFT_TYPES.includes(type)) return roundedNumber(material[SQFT_KEY]);
  return 0;
};

/**
 * Total install size for a job — the sum over its Materials[] rows.
 * @param {Array} materials - ActiveQueue `Materials` array (may be missing)
 * @returns {number}
 */
export const computeJobQtyFromMaterials = (materials) => {
  if (!Array.isArray(materials)) return 0;
  return materials.reduce((total, material) => total + getMaterialQty(material), 0);
};
